import { WorkflowVersion } from "@/components/Workflows/types";
import { ListPromptVersion } from "@/types";
import { ReportScore } from "@/types/evaluate";
import { classNames } from "@/utils/strings";
import { Tab } from "@headlessui/react";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@radix-ui/react-tooltip";

import { Link } from "react-router-dom";
import CommitMessage from "./CommitMessage";
import DroppableVersionLabels from "./DroppableVersionLabels";
import EvaluationStatus from "./EvaluationStatus";
import useVersionDetails from "./hooks/useVersionDetails";
import { Label } from "./types";
import VersionHeader from "./VersionHeader";
import VersionMetadata from "./VersionMetadata";
import VersionTooltipContent from "./VersionTooltipContent";

interface VersionTabProps {
  version: WorkflowVersion | ListPromptVersion;
  isActiveVersion: boolean;
  versionNumber: string | undefined;
  counts: { [index: number]: number };
  navigateToVersionLink: (versionNumber: number) => string;
  onAddReleaseLabel: (label: string, versionId: number) => Promise<unknown>;
  handleDynamicReleaseLabelClick: (release_label_group_id: number) => void;
  dynamicReleaseLabelsRegistry: {
    [version: number]: Label[];
  };
  versionEvaluationScoresRegistry: { [key: number]: ReportScore | null };
  handleABTestButtonClick: (promptId: number, promptVersionId: number) => void;
  promptId: string | undefined;
  isWorkflow: boolean;
  setLabelToDelete: React.Dispatch<React.SetStateAction<Label | null>>;
  setIsConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  allLabels: any[];
}

const VersionTab = ({
  version,
  isActiveVersion,
  versionNumber,
  counts,
  navigateToVersionLink,
  onAddReleaseLabel,
  handleDynamicReleaseLabelClick,
  dynamicReleaseLabelsRegistry,
  versionEvaluationScoresRegistry,
  handleABTestButtonClick,
  promptId,
  isWorkflow,
  setLabelToDelete,
  setIsConfirmationModalOpen,
  allLabels,
}: VersionTabProps) => {
  const versionDetails = useVersionDetails(version);
  const dynamicReleaseLabels = dynamicReleaseLabelsRegistry[version.id] || [];

  return (
    <Tab
      key={versionDetails.id}
      data-version-number={versionDetails.number}
      className={classNames(
        "group w-full select-none overflow-x-hidden outline-none",
        isActiveVersion ? "bg-blue-100" : "",
      )}
    >
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={navigateToVersionLink(versionDetails.number)}
              className="relative block"
            >
              <div className="relative mx-6 border-l border-gray-300 p-2 text-start">
                <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-gray-500 bg-white"></div>

                <VersionHeader number={versionDetails.number} counts={counts} />
                <VersionMetadata
                  created_at={versionDetails.created_at}
                  author={versionDetails.author}
                />

                {versionDetails.commit_message && (
                  <CommitMessage message={versionDetails.commit_message} />
                )}

                <EvaluationStatus
                  id={versionDetails.id}
                  hasReportId={!!versionDetails.hasReportId}
                  versionEvaluationScoresRegistry={
                    versionEvaluationScoresRegistry
                  }
                />

                <DroppableVersionLabels
                  versionId={versionDetails.id}
                  labels={versionDetails.labels}
                  dynamicReleaseLabels={dynamicReleaseLabels}
                  handleDynamicReleaseLabelClick={
                    handleDynamicReleaseLabelClick
                  }
                  isActiveVersion={isActiveVersion}
                  onAddReleaseLabel={onAddReleaseLabel}
                  setLabelToDelete={setLabelToDelete}
                  setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                />
              </div>
            </Link>
          </TooltipTrigger>

          <VersionTooltipContent
            isWorkflow={isWorkflow}
            id={versionDetails.id}
            dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry}
            allLabels={allLabels}
            promptId={promptId}
            handleABTestButtonClick={handleABTestButtonClick}
            version={version}
            versionNumber={versionNumber}
            number={versionDetails.number}
          />
        </Tooltip>
      </TooltipProvider>
    </Tab>
  );
};

export default VersionTab;
